import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import { BLOCKS } from '@contentful/rich-text-types';
import {
  Layout,
  Title,
  Content,
  Card,
  CallToAction,
  OneSidedCard,
  CalendarSection,
  ContactCard,
  AccordionChild,
  Form,
  FoodCardSection,
  AccordionSection,
  CardSection,
  SignupForm,
  Seo,
} from '../components';

import BookingWidget from '../components/booking-widget/BookingWidget';

const Page = (props) => {
  const { translations, locale } = props.pageContext;
  const {
    hero,
    pagetitle,
    subtitle,
    content,
    slug,
    title,
    metaDescription,
    metaImage,
  } = props.data.page;
  const { mainNavigation = [] } = props.data.settings;
  const { followUsFooter = [] } = props.data.settings;
  const { legalTextsFooter } = props.data.settings;
  const { backTo } = props.data.settings;
  const { topTitle } = props.data.settings;
  const footer = {
    followUs: followUsFooter,
    legalTexts: legalTextsFooter,
  };

  const [finnishLanguage, setFinnishLanguage] = useState(true);
  useEffect(() => {
    const currentURL = window.location.href;
    if (!currentURL.includes('/fi/') && finnishLanguage) {
      setFinnishLanguage(false);
    }
  }, [finnishLanguage]);

  const options = {
    renderNode: {
      [BLOCKS.HEADING_1]: (node, children) => {
        return <h1>{children}</h1>;
      },
      [BLOCKS.HEADING_2]: (node, children) => {
        return <h2>{children}</h2>;
      },
      [BLOCKS.HEADING_3]: (node, children) => {
        return <h3>{children}</h3>;
      },
      [BLOCKS.HEADING_4]: (node, children) => {
        return <h4>{children}</h4>;
      },
      [BLOCKS.HEADING_5]: (node, children) => {
        return <h5>{children}</h5>;
      },
      [BLOCKS.HEADING_6]: (node, children) => {
        return <h6>{children}</h6>;
      },
      [BLOCKS.PARAGRAPH]: (node, children) => {
        return <p>{children}</p>;
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const componentProps = node.data.target;
        const component = componentProps && componentProps.internal.type;
        if (component === 'ContentfulCard') {
          return (
            <Card
              image={componentProps.image}
              title={componentProps.title}
              date={componentProps.eventDate}
              text={componentProps.content}
              extra={componentProps.extra}
              actions={{
                primary: {
                  label: componentProps.buttonText,
                  action: componentProps.buttonLink,
                },
              }}
              flagText={componentProps.flagText}
              locale={componentProps['node_locale']}
            />
          );
        } else if (component === 'ContentfulCallToAction') {
          return (
            <CallToAction
              title={componentProps.actionTitle}
              text={componentProps.content}
              action={{
                label: componentProps.buttonText,
                action: componentProps.buttonLink,
              }}
            />
          );
        } else if (component === 'ContentfulOneSidedCard') {
          return (
            <OneSidedCard
              image={componentProps.image}
              position={componentProps.imageSide}
              title={componentProps.title}
              text={componentProps.text}
              finnishLanguage={finnishLanguage}
            />
          );
        } else if (component === 'ContentfulCalendarSection') {
          return (
            <CalendarSection
              title={componentProps.title}
              url={componentProps.url}
            />
          );
        } else if (component === 'ContentfulSecondaryTitle') {
          return (
            <Title
              title={componentProps.title}
              content={componentProps.subtitle?.subtitle || ''}
              primarySubtitle={
                componentProps.subtitleColor
                  ? componentProps.subtitleColor
                  : false
              }
            />
          );
        } else if (component === 'ContentfulContactCard') {
          const contact = {
            email: componentProps.email,
            phone: componentProps.phone,
          };
          return (
            <ContactCard
              image={componentProps.image}
              name={componentProps.name}
              job={componentProps.job}
              contact={contact}
            />
          );
        } else if (component === 'ContentfulAccordionItem') {
          return (
            <AccordionChild
              title={componentProps.title}
              text={componentProps.accordionText}
              finnishLanguage={finnishLanguage}
            />
          );
        } else if (component === 'ContentfulGroupBookingForm') {
          return (
            <Form
              name={componentProps.name}
              title={componentProps.title}
              fields={componentProps.formFields}
              action={{
                label: componentProps.buttonText,
                action: componentProps.buttonLink,
              }}
              successfulSending={componentProps.successfulSending}
              failedSending={componentProps.failedSending}
              marketing={componentProps.marketing}
              mailingLists={componentProps.mailingLists}
              emailProps={componentProps.emailJs}
            />
          );
        } else if (component === 'ContentfulFoodMenuSection') {
          return (
            <FoodCardSection
              title={componentProps.title}
              menus={componentProps.menus}
            />
          );
        } else if (component === 'ContentfulSignupForm') {
          return (
            <SignupForm
              title={componentProps.title}
              formField={componentProps.formField}
              buttonText={componentProps.buttonText}
              disclaimer={componentProps.disclaimer}
              mailingLists={componentProps.mailingLists}
              returnMessage={componentProps.returnMessage}
            />
          );
        } else if (component === 'ContentfulBookingWidget') {
          return (
            <BookingWidget
              eventTypes={componentProps.eventType || []}
              info={componentProps.info || {}}
              language={locale}
            />
          );
        }
      },
    },
  };

  const childrenContent = renderRichText(content, options);
  const groupedChildren = childrenContent.reduce((results, item) => {
    if (!item) return results;
    if (
      results.length &&
      item.type.name &&
      results[results.length - 1][0].type.name &&
      (results[results.length - 1][0].type.name === Card.name ||
        results[results.length - 1][0].type.name === ContactCard.name) &&
      results[results.length - 1][0].type.name === item.type.name
    ) {
      results[results.length - 1].push(item);
    } else if (
      results.length &&
      item.type.name &&
      results[results.length - 1][0].type.name &&
      results[results.length - 1][0].type.name === AccordionChild.name &&
      results[results.length - 1][0].type.name === item.type.name
    ) {
      results[results.length - 1].push(item);
    } else {
      results.push([item]);
    }

    return results;
  }, []);

  let cardSectionQuantity = 0;
  const structuredChildren = groupedChildren.map((item) => {
    if (item.length > 1) {
      if (item[0].type.name === AccordionChild.name) {
        return (
          <AccordionSection key="accordionSection">{item}</AccordionSection>
        );
      } else {
        cardSectionQuantity += 1;
        return (
          <CardSection key={`cardSection${cardSectionQuantity}`}>
            {item}
          </CardSection>
        );
      }
    } else {
      return item[0];
    }
  });

  const movingTitle = content.references.filter((item) => {
    return item.internal.type === 'ContentfulMovingContent';
  });
  return (
    <Layout
      heroImage={hero}
      navigation={mainNavigation}
      slug={slug}
      translations={translations || []}
      locale={locale}
      footer={footer}
      backTo={backTo}
      finnishLanguage={finnishLanguage}
      topTitle={topTitle}
    >
      <Seo
        title={title}
        description={metaDescription}
        image={metaImage}
        locale={locale}
        slug={slug}
      />

      <Title
        title={pagetitle}
        content={subtitle?.subtitle || ''}
        center
        movingTitle={movingTitle.length ? content : ''}
        finnishLanguage={movingTitle.length ? finnishLanguage : false}
      />

      {content && <Content slug={slug}>{structuredChildren}</Content>}
    </Layout>
  );
};

Page.propTypes = {
  props: PropTypes.object,
};

export default Page;

export const query = graphql`
  query ($page_id: String!, $settings_id: String!, $locale: String!) {
    page: contentfulPage(id: { eq: $page_id }, node_locale: { eq: $locale }) {
      ...ContentfulPageFragment
    }

    settings: contentfulGeneralSettings(id: { eq: $settings_id }) {
      ...SettingsFragment
    }
  }
`;
